import { MouseEventHandler, useCallback, useRef, useState } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_PROJECT_SHARE } from '@shared/constants';
import { Box, Theme } from '@mui/material';
import { GhostButton } from '@ui/GhostButton';
import { FormattedMessage, useIntl } from 'react-intl';
import LinkIcon from '@mui/icons-material/Link';
import { v4 } from 'uuid';
import { Icon } from '@ui/Icon';
import { Menu } from '@ui/menus/Menu';
import { MenuItem } from '@ui/menus/Menu/MenuItem';
import { useProjectState } from '@client/store/project';
import { makeStyles } from 'tss-react/mui';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import {
  useDebouncedSnackbar,
  useSnackbar,
} from '@client/hooks/use-snackbar.hooks';
import {
  CLIPBOARD_WRITE_DENIED_MESSAGE,
  CLIPBOARD_WRITE_DENIED_MESSAGE_ID,
  copyTextToClipboard,
} from '@shared/helpers/copy-paste.helpers';
import { wrapError } from '@shared/helpers/utils.helpers';
import { Page } from '@client/store/ui/ui.model';
import {
  useSelectedElementId,
  useSelectedVersionId,
  useUIState,
} from '@client/store/ui';
import {
  SELECTED_ELEMENT_SEARCH_PARAM,
  URL_CATEGORY_NONE,
  type ProjectQueryParams,
} from '@client/models/router.model';
import { useIsSharedProjectRoute } from '@client/hooks/use-router.hooks';
import { useProjectQueryParams } from '@client/hooks/use-router-query-params.hooks';
import { appendProjectQueryParamsToHref } from '@client/helpers/project-query-url.helpers';

export const ShareProjectButton = () => {
  const {
    project: { sharing_key, active_version_id },
  } = useProjectState('project', 'updateProjectDetails');
  const { selectedPage } = useUIState('selectedPage');
  const selectedVersionId = useSelectedVersionId();
  const selectedElementId = useSelectedElementId();
  const isSharedProjectRoute = useIsSharedProjectRoute();
  const { data: projectQueryParams } = useProjectQueryParams(
    isSharedProjectRoute ? 'shared' : 'project',
  );

  const { classes } = useStyles();
  const enqueueSnackbar = useDebouncedSnackbar();
  const errorHandler = useErrorHandler();
  const intl = useIntl();

  const url = sharing_key
    ? getUrl({
        sharingKey: sharing_key,
        versionId: selectedVersionId ?? active_version_id,
        page: selectedPage,
        elementId: selectedElementId,
        queryParams: projectQueryParams,
      })
    : '';

  const copyToClipboard = useCallback(
    (urlToCopy: string) => {
      if (!urlToCopy) return;
      return copyTextToClipboard(urlToCopy, {
        onCopied: () => {
          enqueueSnackbar('Copied to clipboard', { variant: 'success' });
        },
        onDenied: () => {
          enqueueSnackbar(
            intl.formatMessage({
              id: CLIPBOARD_WRITE_DENIED_MESSAGE_ID,
              defaultMessage: CLIPBOARD_WRITE_DENIED_MESSAGE,
            }),
            { variant: 'error' },
          );
        },
        onUnexpected: (error) => {
          errorHandler(wrapError(error));
        },
      });
    },
    [enqueueSnackbar, errorHandler, intl],
  );

  return (
    <Box className={classes.container}>
      {url ? (
        <SharingLink copyToClipboard={copyToClipboard} url={url} />
      ) : (
        <SharingButton copyToClipboard={copyToClipboard} />
      )}
    </Box>
  );
};

type SharingLinkProps = {
  url: string;
  copyToClipboard: (url: string) => void;
};

const SharingLink = ({ url, copyToClipboard }: SharingLinkProps) => {
  const {
    project: { id },
    updateProjectDetails,
  } = useProjectState('project', 'updateProjectDetails');

  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleCopyLink = useCallback(() => {
    copyToClipboard(url);
    setMenuOpen(false);
  }, [copyToClipboard, url]);

  const handleStopSharing = useCallback(() => {
    updateProjectDetails({ reference: id, data: { sharing_key: null } });
    enqueueSnackbar(
      intl.formatMessage({
        id: 'projects_panel.sharing_stopped',
        defaultMessage: 'Sharing stopped',
      }),
    );
    setMenuOpen(false);
  }, [updateProjectDetails, id, enqueueSnackbar, intl]);

  return (
    <>
      <div ref={anchorRef}>
        <GhostButton
          onClick={() => setMenuOpen((prev) => !prev)}
          startIcon={<LinkIcon sx={{ width: 12, height: 12, opacity: 0.4 }} />}
        >
          <FormattedMessage
            id="projects_panel.shared"
            defaultMessage="Shared"
          />
        </GhostButton>
      </div>
      <Menu
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleCopyLink}>
          <FormattedMessage
            id="projects_panel.copy_shared_link"
            defaultMessage="Copy shared link"
          />
        </MenuItem>
        <MenuItem onClick={handleStopSharing}>
          <FormattedMessage
            id="projects_panel.stop_sharing"
            defaultMessage="Stop sharing"
          />
        </MenuItem>
      </Menu>
    </>
  );
};

type SharingButtonProps = {
  copyToClipboard: (url: string) => void;
};

const SharingButton = ({ copyToClipboard }: SharingButtonProps) => {
  const {
    project: { id, active_version_id },
    updateProjectDetails,
  } = useProjectState('project', 'updateProjectDetails');
  const { selectedPage } = useUIState('selectedPage');
  const selectedVersionId = useSelectedVersionId();
  const selectedElementId = useSelectedElementId();
  const isSharedProjectRoute = useIsSharedProjectRoute();
  const { data: projectQueryParams } = useProjectQueryParams(
    isSharedProjectRoute ? 'shared' : 'project',
  );

  const sharing_key = v4();

  const url = getUrl({
    sharingKey: sharing_key,
    versionId: selectedVersionId ?? active_version_id,
    page: selectedPage,
    elementId: selectedElementId,
    queryParams: projectQueryParams,
  });

  const generateSharingLink: MouseEventHandler<HTMLButtonElement> =
    useCallback(async () => {
      await updateProjectDetails({ reference: id, data: { sharing_key } });
      amplitudeLog(AMP_PROJECT_SHARE, { ProjectId: id });
      copyToClipboard(url);
    }, [updateProjectDetails, id, sharing_key, copyToClipboard, url]);

  return (
    <GhostButton
      onClick={generateSharingLink}
      startIcon={
        <Box sx={{ display: 'flex', opacity: 0.4 }}>
          <Icon category="ui" icon="share" size="small" />
        </Box>
      }
    >
      <FormattedMessage id="projects_panel.share" defaultMessage="Share" />
    </GhostButton>
  );
};

type GetUrlOptions = {
  sharingKey: string;
  versionId?: string;
  page?: Page;
  elementId?: string;
  queryParams: ProjectQueryParams;
};

/**
 * Builds the canonical shared-project URL for the current view, including
 * {@link ProjectQueryParams} (e.g. selected proposal) from the active route.
 */
const getUrl = ({
  sharingKey,
  versionId = 'none',
  page = Page.Design,
  elementId,
  queryParams,
}: GetUrlOptions) => {
  const shareElementId =
    page === Page.Elements || page === Page.Diff || page === Page.Products
      ? (elementId ?? URL_CATEGORY_NONE)
      : URL_CATEGORY_NONE;
  const path = `${window.location.origin}/shared/${sharingKey}/v/${versionId}/${page}`;
  const withSelected =
    shareElementId !== URL_CATEGORY_NONE
      ? `${path}?${SELECTED_ELEMENT_SEARCH_PARAM}=${encodeURIComponent(shareElementId)}`
      : path;
  return appendProjectQueryParamsToHref(withSelected, queryParams);
};

const useStyles = makeStyles()((_theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
}));
